/**
 * Rules for putting several files into a project at once (QUERI-852).
 *
 * Each file goes up in its own request to the one-file endpoint, a few at a
 * time. One request per file keeps every file under the ingress's 50 MB body
 * cap, gives each file its own progress and error, and means one bad file
 * never fails the rest.
 */

/** What a project takes; the file pickers' `accept` is built from this */
export const PROJECT_FILE_EXTENSIONS = ['.pdf', '.doc', '.docx', '.txt', '.md'] as const;
export const PROJECT_FILE_ACCEPT = PROJECT_FILE_EXTENSIONS.join(',');

/** Same limit as the backend (and the ingress's proxy-body-size) */
export const MAX_PROJECT_FILE_MB = 50;
export const MAX_PROJECT_FILE_BYTES = MAX_PROJECT_FILE_MB * 1024 * 1024;

/** Most files one pick or drop uploads */
export const MAX_FILES_PER_UPLOAD = 20;

/**
 * Requests in flight at once. The backend holds each file in memory while it
 * stores it, so this stays low; most of the speed-up comes from the first few.
 */
export const UPLOAD_CONCURRENCY = 3;

export type SkipReason = 'type' | 'size' | 'empty' | 'count';

export interface ScreenedFiles {
  accepted: File[];
  skipped: { file: File; reason: SkipReason }[];
}

const extensionOf = (name: string): string => {
  const dot = name.lastIndexOf('.');
  return dot < 0 ? '' : name.slice(dot).toLowerCase();
};

/**
 * Splits picked or dropped files into the ones to upload and the ones to skip,
 * with the reason. A drop bypasses the picker's `accept`, and a dropped folder
 * arrives as an empty file, so both are checked here.
 */
export function screenFiles(
  files: ArrayLike<File>,
  maxFiles: number = MAX_FILES_PER_UPLOAD,
): ScreenedFiles {
  const accepted: File[] = [];
  const skipped: ScreenedFiles['skipped'] = [];
  for (const file of Array.from(files)) {
    const reason: SkipReason | null = !(PROJECT_FILE_EXTENSIONS as readonly string[]).includes(
      extensionOf(file.name),
    )
      ? 'type'
      : file.size === 0
        ? 'empty'
        : file.size > MAX_PROJECT_FILE_BYTES
          ? 'size'
          : accepted.length >= maxFiles
            ? 'count'
            : null;
    if (reason) skipped.push({ file, reason });
    else accepted.push(file);
  }
  return { accepted, skipped };
}

/**
 * Runs `worker` over `items` with at most `limit` running at once and resolves
 * to the results in the order of `items`. A worker that throws rejects the
 * whole run, so workers should catch their own errors.
 */
export async function runWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  worker: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  const lane = async () => {
    while (next < items.length) {
      const index = next++;
      results[index] = await worker(items[index], index);
    }
  };
  const lanes = Math.max(1, Math.min(limit, items.length));
  await Promise.all(Array.from({ length: lanes }, lane));
  return results;
}

/** Share of the batch's bytes sent so far, 0-100, so one large file isn't hidden by many small ones */
export function overallProgress(entries: readonly { size: number; progress: number }[]): number {
  const total = entries.reduce((sum, e) => sum + e.size, 0);
  if (total === 0) return 0;
  const sent = entries.reduce((sum, e) => sum + (e.size * e.progress) / 100, 0);
  return Math.round((sent * 100) / total);
}
